import jsPDF from "jspdf";
import html2canvas from "html2canvas";
import { attT } from "@/lib/attendanceI18n";

/**
 * Export the monthly timesheet as PDF — language-perfect.
 *
 * Why html2canvas (not autoTable):
 *   jsPDF's built-in text engine ships only Latin fonts. Arabic glyphs
 *   render as broken boxes/reversed letters. We work around this by
 *   rendering a hidden HTML twin of the table using the same Tajawal
 *   webfont as the live UI, then snapshotting it into the PDF. The end
 *   result matches what the user sees on screen pixel-for-pixel and
 *   works for BOTH Arabic and English.
 */
export async function exportTimesheetPdf({ lang, year, month, rows, days, statusViz, monthName, kpis }) {
  const dir = lang === "ar" ? "rtl" : "ltr";
  const sandbox = document.createElement("div");
  Object.assign(sandbox.style, {
    position: "fixed",
    insetInlineStart: "-99999px",
    top: "0",
    background: "#ffffff",
    color: "#000000",
    padding: "16px 18px",
    fontFamily: "'Tajawal','Cairo','Segoe UI',sans-serif",
    width: "1400px",
  });
  sandbox.setAttribute("dir", dir);

  const title = lang === "ar"
    ? `كشف الحضور — ${monthName} ${year}`
    : `Timesheet — ${monthName} ${year}`;
  const generated = lang === "ar"
    ? `تاريخ الإصدار: ${new Date().toLocaleDateString("en-US")}`
    : `Generated: ${new Date().toLocaleDateString("en-US")}`;

  // ── Helpers ────────────────────────────────────────────
  const fmtRiyadh = (iso) => {
    if (!iso) return "";
    try {
      const d = new Date(iso);
      const utcMs = d.getTime() + d.getTimezoneOffset() * 60_000;
      const local = new Date(utcMs + 3 * 60 * 60_000);
      return `${String(local.getHours()).padStart(2, "0")}:${String(local.getMinutes()).padStart(2, "0")}`;
    } catch { return ""; }
  };

  // ── KPI strip ─────────────────────────────────────────
  let kpiHtml = "";
  if (kpis) {
    const hh = Math.floor((kpis.workedMinutes || 0) / 60);
    const mm = (kpis.workedMinutes || 0) % 60;
    const labels = lang === "ar"
      ? { hours: "إجمالي الساعات", present: "أيام الحضور", absent: "أيام الغياب", lateCt: "مرات التأخر", lateMin: "دقائق التأخر" }
      : { hours: "Total hours", present: "Days present", absent: "Days absent", lateCt: "Late incidents", lateMin: "Late minutes" };
    const card = (color, title, value) => `
      <div style="flex:1;min-width:140px;background:#fff;border:1px solid #ddd;border-${dir === "rtl" ? "right" : "left"}:4px solid ${color};border-radius:8px;padding:8px 12px;">
        <div style="font-size:10px;color:#555;font-weight:700;margin-bottom:3px;">${title}</div>
        <div style="font-size:14px;font-weight:900;color:#000;">${value}</div>
      </div>`;
    kpiHtml = `
      <div style="display:flex;gap:8px;margin-bottom:12px;flex-wrap:wrap;">
        ${card("#16a34a", labels.hours,   `${hh}:${String(mm).padStart(2,"0")}`)}
        ${card("#0891b2", labels.present, `${kpis.present}`)}
        ${card("#dc2626", labels.absent,  `${kpis.absent}`)}
        ${card("#f59e0b", labels.lateCt,  `${kpis.lateCount}`)}
        ${card("#f97316", labels.lateMin, `${kpis.lateMinutes}`)}
      </div>`;
  }

  // Build the table HTML
  const head = `
    <tr style="background:#d4af37;color:#000;font-weight:800;">
      <th style="padding:6px 8px;text-align:${dir === "rtl" ? "right" : "left"};border:1px solid #aaa;">${attT(lang, "ts.col.employee")}</th>
      <th style="padding:6px 8px;text-align:center;border:1px solid #aaa;">${attT(lang, "ts.col.shift")}</th>
      ${days.map((d) => `<th style="padding:6px 4px;text-align:center;border:1px solid #aaa;font-size:10px;min-width:48px;">${d}</th>`).join("")}
      <th style="padding:6px 6px;text-align:center;border:1px solid #aaa;background:#a6841c;color:#fff;">${attT(lang, "ts.col.present")}</th>
      <th style="padding:6px 6px;text-align:center;border:1px solid #aaa;background:#a6841c;color:#fff;">${attT(lang, "ts.col.absent")}</th>
      <th style="padding:6px 6px;text-align:center;border:1px solid #aaa;background:#a6841c;color:#fff;">${attT(lang, "ts.col.late")}</th>
    </tr>`;

  const body = rows.map((r) => {
    const cells = days.map((d) => {
      const c = r.cells[d];
      const v = statusViz[c?.status] || statusViz.unrecorded;
      const ramadan = c?.is_ramadan ? "outline:1.5px dashed #facc15;outline-offset:-2px;" : "";
      const inT = fmtRiyadh(c?.check_in_at);
      const outT = fmtRiyadh(c?.check_out_at);
      const hasTimes = inT || outT;
      const content = hasTimes
        ? `<div style="display:flex;flex-direction:column;line-height:1.15;font-size:9px;font-weight:800;">
             ${inT  ? `<span style="color:#bbf7d0;">● ${inT}</span>` : ""}
             ${outT ? `<span style="color:#bfdbfe;">● ${outT}</span>` : ""}
           </div>`
        : `<span style="font-size:13px;font-weight:800;">${v.sym}</span>`;
      return `<td style="text-align:center;border:1px solid #aaa;padding:3px 2px;background:${v.bg};color:${v.color};${ramadan}min-width:48px;">${content}</td>`;
    }).join("");
    return `<tr>
      <td style="padding:6px 8px;border:1px solid #aaa;font-weight:800;text-align:${dir === "rtl" ? "right" : "left"};">
        ${escapeHtml(r.name)}<br/><span style="font-size:9px;color:#666;">@${escapeHtml(r.username)}</span>
      </td>
      <td style="padding:6px 6px;border:1px solid #aaa;text-align:center;font-size:11px;">${attT(lang, r.shift === "evening" ? "ts.shift.evening" : "ts.shift.morning")}</td>
      ${cells}
      <td style="padding:6px 6px;border:1px solid #aaa;text-align:center;font-weight:800;background:#f3f4f6;">${r.totals.present + r.totals.half}</td>
      <td style="padding:6px 6px;border:1px solid #aaa;text-align:center;font-weight:800;background:#fee2e2;color:#b91c1c;">${r.totals.absent}</td>
      <td style="padding:6px 6px;border:1px solid #aaa;text-align:center;font-weight:800;background:#fef3c7;color:#92400e;">${r.totals.late_count}</td>
    </tr>`;
  }).join("");

  // Legend (uses Arabic / English from i18n)
  const legendKeys = ["present", "half", "absent", "excused", "approved_leave", "permitted", "sick", "off"];
  const legend = legendKeys.map((k) => {
    const v = statusViz[k];
    return `<span style="display:inline-flex;align-items:center;gap:4px;padding:3px 8px;border-radius:999px;background:${v.bg};color:${v.color};font-size:11px;font-weight:700;margin:0 4px 4px 0;">
      <span style="font-weight:900;">${v.sym}</span> ${attT(lang, `status.${k}`)}
    </span>`;
  }).join("");

  sandbox.innerHTML = `
    <div style="border-bottom:2px solid #d4af37;padding-bottom:8px;margin-bottom:12px;display:flex;justify-content:space-between;align-items:baseline;">
      <h2 style="margin:0;font-size:18px;color:#000;font-weight:900;">${title}</h2>
      <small style="color:#555;font-size:11px;">${generated}</small>
    </div>
    ${kpiHtml}
    <div style="margin-bottom:10px;">${legend}</div>
    <table style="border-collapse:collapse;width:100%;font-size:11px;">
      <thead>${head}</thead>
      <tbody>${body}</tbody>
    </table>
  `;
  document.body.appendChild(sandbox);

  try {
    // Wait one frame so webfonts apply
    await new Promise((r) => requestAnimationFrame(r));
    if (document.fonts?.ready) {
      try { await document.fonts.ready; } catch { /* ignore */ }
    }
    const canvas = await html2canvas(sandbox, {
      scale: 2, backgroundColor: "#ffffff", useCORS: true, logging: false,
    });
    const imgData = canvas.toDataURL("image/png");
    const doc = new jsPDF({ orientation: "landscape", unit: "mm", format: "a4" });
    const pageW = doc.internal.pageSize.getWidth();
    const pageH = doc.internal.pageSize.getHeight();
    // Fit image while keeping aspect ratio
    const imgRatio = canvas.width / canvas.height;
    let drawW = pageW - 10;
    let drawH = drawW / imgRatio;
    if (drawH > pageH - 10) {
      drawH = pageH - 10;
      drawW = drawH * imgRatio;
    }
    doc.addImage(imgData, "PNG", (pageW - drawW) / 2, 5, drawW, drawH);
    doc.save(`timesheet_${year}_${String(month).padStart(2, "0")}.pdf`);
  } finally {
    document.body.removeChild(sandbox);
  }
}

function escapeHtml(s) {
  return String(s || "").replace(/[&<>"']/g, (ch) => ({
    "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;",
  }[ch]));
}
